//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isTruthy } from '@utils/filterPredicates';

interface TypeaheadSearchOption {
  externalID: string;
  name: string;
}

interface TypeaheadDropdownContentProps {
  allowCustomValue: boolean;
  isLoading: boolean;
  onCustomValueSelect: (customValue: string) => void;
  onSearch: (searchTerm: string) => void;
  onSelect: (value: string) => void;
  options: TypeaheadSearchOption[];
  queryError: Error | null;
  resourceName: string;
  searchKeys: string[];
  searchPlaceholder: string;
  trackingIdPrefix: TrackingIdPrefix;
  trimmedSearch: string;
  value: string;
  visuallyHiddenLabel: string;
}

export const TypeaheadDropdownContent = ({
  allowCustomValue,
  isLoading,
  onCustomValueSelect,
  onSearch,
  onSelect,
  options,
  queryError,
  resourceName,
  searchKeys,
  searchPlaceholder,
  trackingIdPrefix,
  trimmedSearch,
  value,
  visuallyHiddenLabel,
}: TypeaheadDropdownContentProps) => {
  return (
    <DropdownMenu.Content align='start'>
      <DropdownMenu.Search
        handleClear={() => onSearch('')}
        handleSearch={onSearch}
        itemsToSearch={options}
        searchBarConfig={{
          placeholder: searchPlaceholder,
          trackingIdPrefix,
          visuallyHiddenLabel,
        }}
        searchKeys={searchKeys}
      />
      {isLoading && <EmptyState>Loading {resourceName}...</EmptyState>}
      {!isLoading && isTruthy(queryError) && (
        <EmptyState>Error loading {resourceName}. Please try again.</EmptyState>
      )}
      {!isLoading && !isTruthy(queryError) && options.length > 0 && (
        <DropdownMenu.RadioGroup onValueChange={onSelect} value={value}>
          {options.map((option) => (
            <DropdownMenu.RadioItem
              key={option.externalID}
              trackingId={`${trackingIdPrefix}option`}
              value={option.name}
            >
              <DropdownMenu.ItemIndicator>
                <DropdownMenu.IndicatorIcon />
              </DropdownMenu.ItemIndicator>
              {option.name}
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      )}
      {allowCustomValue && (
        <DropdownMenu.Item
          key={`custom-${trimmedSearch}`}
          onSelect={() => onCustomValueSelect(trimmedSearch)}
          trackingId={`${trackingIdPrefix}add-custom`}
        >
          Add &quot;{trimmedSearch}&quot;
        </DropdownMenu.Item>
      )}
    </DropdownMenu.Content>
  );
};

const EmptyState = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]}
      calc(${theme.spacings[2]} + ${theme.spacings[1]});
    color: ${theme.colors.gray5};
    text-align: center;
  `,
);
